// src/contexts/AppDataContext.tsx - ROLE-BASED LOADING VERSION
// This context contains ONLY data that ALL roles need:
// - Reference data (universities, locations, terms, work scopes)
// - Modules (auto-loaded for CANDIDATES, lazy for others)
// - Search functions

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { CandidateProfileService } from '@/services/CandidateProfileService';
import { ModuleService } from '@/services/ModuleService';
import {
    UniversityOption,
    LocationOption,
    WorkScopeOption,
} from '@/lib/models/candidateProfile';
import {
    Module,
    Case,
    ModuleWithCases,
    UIModule,
    CreateModuleRequest,
    UpdateModuleRequest,
    PaginationRequest
} from '@/lib/models/module';
import { useAuth } from '@/contexts/AuthContext';

interface AppDataContextType {
    // ==================== REFERENCE DATA ====================
    universities: UniversityOption[];
    locations: LocationOption[];
    workScopeOptions: WorkScopeOption[];

    // Loading states for reference data
    isLoadingUniversities: boolean;
    isLoadingLocations: boolean;
    isLoadingOptions: boolean;

    // ==================== COURSE DATA ====================
    modules: Module[];
    uiModules: UIModule[];
    ongoingModules: UIModule[];
    availableModules: UIModule[];
    moduleCategories: string[];
    isLoadingModules: boolean;
    isSavingModule: boolean;
    moduleError: string | null;

    // ==================== SEARCH FUNCTIONS ====================
    searchUniversities: (query: string) => Promise<UniversityOption[]>;
    searchLocations: (query: string) => Promise<LocationOption[]>;

    // ==================== COURSE FUNCTIONS ====================
    getModules: (pagination?: PaginationRequest) => Promise<Module[]>;
    getModuleById: (moduleId: string) => Promise<Module | null>;
    getModuleWithCases: (moduleId: string) => Promise<ModuleWithCases | null>;
    createModule: (moduleData: CreateModuleRequest) => Promise<Module | null>;
    updateModule: (moduleId: string, moduleData: UpdateModuleRequest) => Promise<Module | null>;
    deleteModule: (moduleId: string) => Promise<void>;
    searchModules: (query: string) => Promise<Module[]>;

    // ==================== REFRESH FUNCTIONS ====================
    refreshReferenceData: () => Promise<void>;
    refreshModules: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    // ==================== REFERENCE DATA STATE ====================
    const [universities, setUniversities] = useState<UniversityOption[]>([]);
    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [workScopeOptions, setWorkScopeOptions] = useState<WorkScopeOption[]>([]);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    // ==================== COURSE STATE ====================
    const [modules, setModules] = useState<Module[]>([]);
    const [uiModules, setUiModules] = useState<UIModule[]>([]);
    const [ongoingModules, setOngoingModules] = useState<UIModule[]>([]);
    const [availableModules, setAvailableModules] = useState<UIModule[]>([]);
    const [moduleCategories, setModuleCategories] = useState<string[]>([]);
    const [isLoadingModules, setIsLoadingModules] = useState(false);
    const [isSavingModule, setIsSavingModule] = useState(false);
    const [moduleError, setModuleError] = useState<string | null>(null);

    // ==================== SEARCH CACHE ====================
    const [universityCache, setUniversityCache] = useState<Map<string, UniversityOption[]>>(new Map());
    const [locationCache, setLocationCache] = useState<Map<string, LocationOption[]>>(new Map());

    // ==================== LOAD REFERENCE DATA ====================

    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                console.log('📊 [AppData] Loading reference data (universities, locations)...');

                // Load universities
                setIsLoadingUniversities(true);
                const unis = await CandidateProfileService.searchUniversities('');
                setUniversities(unis);

                // Load locations
                setIsLoadingLocations(true);
                const locs = await CandidateProfileService.searchLocations('');
                setLocations(locs);

                // Load static options
                setIsLoadingOptions(true);
                const workScope = CandidateProfileService.getWorkScopeOptions();

                setWorkScopeOptions(workScope);

                console.log('✅ [AppData] Reference data loaded successfully');
            } catch (error) {
                console.error('❌ [AppData] Error loading reference data:', error);
            } finally {
                setIsLoadingUniversities(false);
                setIsLoadingLocations(false);
                setIsLoadingOptions(false);
            }
        };

        loadReferenceData();
    }, []);

    // ==================== ROLE-BASED COURSE LOADING ====================
    // ✅ Auto-load for CANDIDATES (they need modules!)
    // ✅ Don't load for EMPLOYERS (they don't need modules!)

    useEffect(() => {
        if (isAuthenticated && user?.id) {
            if (user.role === 'candidate') {
                console.log('📚 [AppData] Candidate user authenticated, loading modules...');
                refreshModules();
            } else {
                console.log(`📚 [AppData] Non-candidate user (${user.role}) authenticated, modules will load on demand`);
            }
        } else {
            console.log('📚 [AppData] User logged out, clearing module data...');
            setModules([]);
            setUiModules([]);
            setOngoingModules([]);
            setAvailableModules([]);
            setModuleCategories([]);
        }
    }, [isAuthenticated, user?.id, user?.role]);

    // ==================== SEARCH FUNCTIONS ====================

    const searchUniversities = useCallback(async (query: string): Promise<UniversityOption[]> => {
        const cacheKey = query.toLowerCase().trim();

        if (universityCache.has(cacheKey)) {
            console.log(`🔍 [AppData] University search cache hit: "${query}"`);
            return universityCache.get(cacheKey)!;
        }

        console.log(`🔍 [AppData] University search API call: "${query}"`);
        const results = await CandidateProfileService.searchUniversities(query);

        setUniversityCache(prev => new Map(prev).set(cacheKey, results));
        return results;
    }, [universityCache]);

    const searchLocations = useCallback(async (query: string): Promise<LocationOption[]> => {
        const cacheKey = query.toLowerCase().trim();

        if (locationCache.has(cacheKey)) {
            console.log(`🔍 [AppData] Location search cache hit: "${query}"`);
            return locationCache.get(cacheKey)!;
        }

        console.log(`🔍 [AppData] Location search API call: "${query}"`);
        const results = await CandidateProfileService.searchLocations(query);

        setLocationCache(prev => new Map(prev).set(cacheKey, results));
        return results;
    }, [locationCache]);

    // ==================== COURSE FUNCTIONS ====================

    const getModules = useCallback(async (pagination?: PaginationRequest): Promise<Module[]> => {
        setIsLoadingModules(true);
        setModuleError(null);

        try {
            console.log('📚 [AppData] Fetching modules...');
            const fetchedModules = await ModuleService.getModules(pagination);
            setModules(fetchedModules);
            console.log(`✅ [AppData] Fetched ${fetchedModules.length} modules`);
            return fetchedModules;
        } catch (error: any) {
            console.error('❌ [AppData] Error fetching modules:', error);
            setModuleError('Failed to fetch modules');
            return [];
        } finally {
            setIsLoadingModules(false);
        }
    }, []);

    const getModuleById = useCallback(async (moduleId: string): Promise<Module | null> => {
        setModuleError(null);

        try {
            console.log(`📚 [AppData] Fetching module: ${moduleId}`);
            const module = await ModuleService.getModuleById(moduleId);
            return module;
        } catch (error: any) {
            console.error('❌ [AppData] Error fetching module:', error);
            setModuleError('Failed to fetch module');
            return null;
        }
    }, []);

    const getModuleWithCases = useCallback(async (moduleId: string): Promise<ModuleWithCases | null> => {
        setModuleError(null);

        try {
            console.log(`📚 [AppData] Fetching module with cases: ${moduleId}`);
            const moduleWithCases = await ModuleService.getModuleWithCases(moduleId);
            return moduleWithCases;
        } catch (error: any) {
            console.error('❌ [AppData] Error fetching module with cases:', error);
            setModuleError('Failed to fetch module with cases');
            return null;
        }
    }, []);

    const createModule = useCallback(async (moduleData: CreateModuleRequest): Promise<Module | null> => {
        setIsSavingModule(true);
        setModuleError(null);

        try {
            console.log('✨ [AppData] Creating module...');
            const newModule = await ModuleService.createModule(moduleData);

            // Add to local state
            setModules(prev => [...prev, newModule]);
            console.log('✅ [AppData] Module created successfully');
            return newModule;
        } catch (error: any) {
            console.error('❌ [AppData] Error creating module:', error);
            setModuleError('Failed to create module');
            return null;
        } finally {
            setIsSavingModule(false);
        }
    }, []);

    const updateModule = useCallback(async (
        moduleId: string,
        moduleData: UpdateModuleRequest
    ): Promise<Module | null> => {
        setIsSavingModule(true);
        setModuleError(null);

        try {
            console.log(`💾 [AppData] Updating module: ${moduleId}`);
            const updatedModule = await ModuleService.updateModule(moduleId, moduleData);

            // Update local state
            setModules(prev => prev.map(c => c.id === moduleId ? updatedModule : c));
            console.log('✅ [AppData] Module updated successfully');
            return updatedModule;
        } catch (error: any) {
            console.error('❌ [AppData] Error updating module:', error);
            setModuleError('Failed to update module');
            return null;
        } finally {
            setIsSavingModule(false);
        }
    }, []);

    const deleteModule = useCallback(async (moduleId: string): Promise<void> => {
        setIsSavingModule(true);
        setModuleError(null);

        try {
            console.log(`🗑️ [AppData] Deleting module: ${moduleId}`);
            await ModuleService.deleteModule(moduleId);

            // Remove from local state
            setModules(prev => prev.filter(c => c.id !== moduleId));
            console.log('✅ [AppData] Module deleted successfully');
        } catch (error: any) {
            console.error('❌ [AppData] Error deleting module:', error);
            setModuleError('Failed to delete module');
            throw error;
        } finally {
            setIsSavingModule(false);
        }
    }, []);

    const searchModules = useCallback(async (query: string): Promise<Module[]> => {
        setModuleError(null);

        try {
            console.log(`🔍 [AppData] Searching modules: "${query}"`);
            const results = await ModuleService.searchModules(query);
            console.log(`✅ [AppData] Found ${results.length} modules`);
            return results;
        } catch (error: any) {
            console.error('❌ [AppData] Error searching modules:', error);
            setModuleError('Failed to search modules');
            return [];
        }
    }, []);

    // ==================== REFRESH FUNCTIONS ====================

    const refreshReferenceData = useCallback(async (): Promise<void> => {
        console.log('🔄 [AppData] Refreshing reference data...');

        try {
            setIsLoadingUniversities(true);
            setIsLoadingLocations(true);
            setIsLoadingOptions(true);

            const [unis, locs] = await Promise.all([
                CandidateProfileService.searchUniversities(''),
                CandidateProfileService.searchLocations('')
            ]);

            const workScope = CandidateProfileService.getWorkScopeOptions();

            setUniversities(unis);
            setLocations(locs);
            setWorkScopeOptions(workScope);

            console.log('✅ [AppData] Reference data refreshed');
        } catch (error) {
            console.error('❌ [AppData] Error refreshing reference data:', error);
        } finally {
            setIsLoadingUniversities(false);
            setIsLoadingLocations(false);
            setIsLoadingOptions(false);
        }
    }, []);

    const refreshModules = useCallback(async (): Promise<void> => {
        console.log('🔄 [AppData] Refreshing modules (all variants)...');

        setIsLoadingModules(true);
        setModuleError(null);

        try {
            const [fetchedModules, uiModulesData, ongoingData, availableData, categories] = await Promise.all([
                ModuleService.getModules(),
                ModuleService.getModulesForUI(),
                ModuleService.getOngoingModules(),
                ModuleService.getAvailableModules(),
                ModuleService.getModuleCategories()
            ]);

            setModules(fetchedModules);
            setUiModules(uiModulesData);
            setOngoingModules(ongoingData);
            setAvailableModules(availableData);
            setModuleCategories(categories);

            console.log('✅ [AppData] Modules refreshed successfully');
        } catch (error: any) {
            console.error('❌ [AppData] Error refreshing modules:', error);
            setModuleError('Failed to refresh modules');
        } finally {
            setIsLoadingModules(false);
        }
    }, []);

    // ==================== CONTEXT VALUE ====================

    const value: AppDataContextType = {
        // Reference data
        universities,
        locations,
        workScopeOptions,
        isLoadingUniversities,
        isLoadingLocations,
        isLoadingOptions,

        // Module data
        modules,
        uiModules,
        ongoingModules,
        availableModules,
        moduleCategories,
        isLoadingModules,
        isSavingModule,
        moduleError,

        // Search functions
        searchUniversities,
        searchLocations,

        // Module functions
        getModules,
        getModuleById,
        getModuleWithCases,
        createModule,
        updateModule,
        deleteModule,
        searchModules,

        // Refresh functions
        refreshReferenceData,
        refreshModules,
    };

    return (
        <AppDataContext.Provider value={value}>
            {children}
        </AppDataContext.Provider>
    );
};

// ==================== HOOK ====================

export const useAppData = (): AppDataContextType => {
    const context = useContext(AppDataContext);
    if (context === undefined) {
        throw new Error('useAppData must be used within an AppDataProvider');
    }
    return context;
};